import React from 'react'
import { Map, List } from 'immutable'
import _get from 'lodash/get'
import _set from 'lodash/set'
import { get as getConfig } from '../../config/index'
import { getWatchAccess } from '../../services/media/index'
import {
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_VIDEO,
  TYPE_CONTENT_PRODUCT,
  TYPE_CONTENT_COURSE,
  isGuideType,
  isEventType,
} from '../../services/content-type/index'
import { getPrimaryCourse } from '../../services/content/index'
import { parse as parseQuery, stringify as stringifyQuery } from '../../services/query-string/index'
import Link from '../Link/index'
import { TARGET_BLANK } from '../Link/constants'
import ProgressBar, { PROGRESS_BAR_PINK_GRADIENT } from '../ProgressBar/index'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from '../Picture/index'
import { checkIfCourseWithoutProgress } from '../DetailPageV3/utils'
import { URL_GAIA_PLUS } from '../../services/url/constants'
import { SUBSCRIPTION_CODE_EVENTS_PLUS } from '../../services/subscription/index'
import Play from '../Icon/icons/Play/index'
import Locked from '../Icon/icons/Locked/index'
import {
  getTileUrl,
  handleTileV2OnClick,
  isGaiaOriginal,
  getHeroImage,
  getImageSizes,
  getTileV2ElementClassName,
  isLiveAccessUpgradeToWatch,
} from './utils'
import {
  TILE_V2_DISPLAY_DEFAULT,
} from './constants'

const config = getConfig()
const optimizedImagesEnabled = !!_get(config, ['features', 'optimizedImages'])

/**
 * The progress bar for a tile (series or video)
 * @param {Object} props - The props
 * @param {Map} props.userNodeInfo - The userNodeInfo for the content
 * @param {Boolean} props.isLoggedIn - If the user is logged in or not
 * @param {String} props.contentType - The type of content
 * @param {Boolean} props.legacyData - Whether or not the data source is coming from a
 *  legacy source (i.e., non member home tile requests)
 * @returns {JSX.Element|null} Progress bar for a tile or null
 */
function TileV2ProgressBar (props) {
  const {
    userNodeInfo = Map(),
    isLoggedIn,
    contentType,
    data,
    legacyData,
  } = props

  if (
    contentType !== TYPE_CONTENT_SERIES
      && contentType !== TYPE_CONTENT_VIDEO
      && contentType !== TYPE_CONTENT_COURSE
  ) {
    return null
  }

  let featuredEpisodeUserNode
  let userNode
  let featuredEpisode

  if (legacyData && contentType === TYPE_CONTENT_SERIES) {
    featuredEpisode = Map.isMap(data.get('featuredEpisode')) ? data.get('featuredEpisode', Map()) : Map()
    featuredEpisodeUserNode = Map.isMap(featuredEpisode.get('userNode')) ? featuredEpisode.get('userNode', Map()) : Map()
  } else {
    featuredEpisode = Map.isMap(userNodeInfo) && Map.isMap(userNodeInfo.get('featuredEpisode')) ? userNodeInfo.get('featuredEpisode', Map()) : Map()
    featuredEpisodeUserNode = Map.isMap(featuredEpisode) ? featuredEpisode.get('userNode', Map()) : Map()
  }

  if (legacyData && contentType === TYPE_CONTENT_VIDEO) {
    userNode = Map.isMap(data.get('userInfo')) ? data.get('userInfo', Map()) : Map()
  } else {
    userNode = Map.isMap(userNodeInfo) ? userNodeInfo.get('data', Map()) : Map()
  }

  const featuredEpisodeDuration = Map.isMap(featuredEpisode) ? featuredEpisode.getIn(['data', 'feature', 'duration'], 0) : 0
  const hasEpisodeUserNodeInfo = Map.isMap(featuredEpisodeUserNode)
    && featuredEpisodeUserNode.size > 0
  const featureDuration = data.getIn(['feature', 'duration'], 0)

  if (isLoggedIn && contentType === TYPE_CONTENT_SERIES && hasEpisodeUserNodeInfo && featuredEpisodeUserNode.getIn(['featurePosition'], 0) && featuredEpisodeDuration) {
    return (
      <div className="tile-v2__progress-bar">
        <ProgressBar
          duration={featuredEpisodeDuration}
          userInfo={featuredEpisodeUserNode}
          type={PROGRESS_BAR_PINK_GRADIENT}
        />
      </div>
    )
  }

  if (userNode.size > 0 && featureDuration && userNode.get('featurePosition')) {
    return (
      <div className="tile-v2__progress-bar">
        <ProgressBar
          duration={featureDuration}
          userInfo={userNode}
          type={PROGRESS_BAR_PINK_GRADIENT}
        />
      </div>
    )
  }

  return null
}

/**
 * The play button for touch devices
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {String} props.contentType - The type of content
 * @param {Boolean} props.upgradeToWatch - Whether or not the user needs to upgrade to watch
 * @param {Boolean} props.watchAccess - Whether or not the user has access to watch
 * @param {String} props.path - The url of the content
 * @returns {JSX.Element|null} Play/action button for touch devices
 */
function TileV2ActionButton (props) {
  const {
    contentType,
    path,
    watchAccess,
    upgradeToWatch,
    data,
    staticText,
  } = props

  if (!data) {
    return null
  }

  if (contentType !== TYPE_CONTENT_SERIES && contentType !== TYPE_CONTENT_VIDEO) {
    return null
  }

  if (upgradeToWatch) {
    return (
      <Link
        className="tile-v2__action-btn tile-v2__action-btn--upgrade-to-watch"
        to={URL_GAIA_PLUS}
        aria-label="Upgrade to watch"
      >
        <Locked size={24} color="#DADFE5" />
      </Link>
    )
  }

  if (watchAccess) {
    return (
      <Link
        className="tile-v2__action-btn tile-v2__action-btn--play"
        to={`${path}?fullplayer=feature`}
        onClick={(e) => handleTileV2OnClick(e, props, true)}
        aria-label={staticText ? staticText.get('watchNow', 'Play') : 'Play'}
      >
        <Play size={38} color="#151515" />
      </Link>
    )
  }

  return null
}

/**
 * Label for a tile (Gaia original, etc.)
 * @param {Object} props - The props
 * @param {Map} props.staticText - The static text
 * @param {String} props.contentType - The type of content
 * @returns {JSX.Element|null} Tile label
 */
function TileV2Label (props) {
  const {
    staticText,
    gaiaOriginal,
    upgradeToWatch,
    contentType,
    contentId,
    isGuide,
    isEvent,
    isGaiaPlus,
  } = props

  const isCourseWithoutProgress = checkIfCourseWithoutProgress(contentId)

  // Gaia+ Label
  const isGaiaPlusGuide = isGaiaPlus && isGuide
  const renderGaiaPlusLabel = isGaiaPlusGuide
  || (isEvent && !isGuide)
  || upgradeToWatch

  if (renderGaiaPlusLabel) {
    return (
      <span className="tile-v2__label tile-v2__label--gaia-plus">
        <span className="tile-v2__label-text">
          {staticText.get('eventsPlus')}
        </span>
      </span>
    )
  }

  if (gaiaOriginal) {
    return (
      <span className="tile-v2__label tile-v2__label--gaia-original">
        <span className="tile-v2__label-text">Gaia | {staticText.get('original')}</span>
      </span>
    )
  }

  if (contentType === TYPE_CONTENT_SERIES) {
    return (
      <span className="tile-v2__label tile-v2__label--series">
        <span className="tile-v2__label-text">
          {staticText.get('series')}
        </span>
      </span>
    )
  }

  if (contentType === TYPE_CONTENT_COURSE && !isCourseWithoutProgress) {
    return (
      <span className="tile-v2__label tile-v2__label--course">
        <span className="tile-v2__label-text">
          {staticText.get('course')}
        </span>
      </span>
    )
  }
  return null
}

/**
 * The tile hero image for a tile
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {String} props.display - The display type (normal, vertical, etc.)
 * @param {String} props.contentType - The type of content
 * @param {Boolean} props.hovered - Whether or not the tile is in the over state
 * @param {Boolean} props.isTouchable - Whether or not the device is touch capable
 * @param {Map} props.userNodeInfo - The userNodeInfo for the content
 * @param {List} props.subscriptions - The users entitlements
 * @param {String} props.country - The user's country iso2
 * @param {Boolean} props.legacyData - Whether or not the data source is coming from a
 * @param {Boolean} props.hideLabel - Whether or not hide label
 * @returns {JSX.Element} The tile hero image
 */
function TileV2Hero (props) {
  const {
    data = Map(),
    display = TILE_V2_DISPLAY_DEFAULT,
    contentType,
    contentSubtype,
    staticText,
    hovered,
    isTouchable,
    legacyData,
    userNodeInfo = Map(),
    subscriptions = List(),
    country,
    hideLabel = false,
    userLanguage,
  } = props
  const image = getHeroImage(props)
  const title = data.get('title', '')
  const isContentTypeProduct = contentType === TYPE_CONTENT_PRODUCT
  const studio = data.get('studio', '')
  const gaiaOriginal = isGaiaOriginal({ studio }) && contentType === TYPE_CONTENT_SERIES
  const isGuide = isGuideType(data)
  const isEvent = isEventType(data)
  let featuredEpisodePath
  let featuredEpisodeFeature
  const imageSizes = getImageSizes(props)
  const imageWidth = _get(imageSizes, 'width', '400')
  const imageHeight = _get(imageSizes, 'height', '225')

  const url = getTileUrl(props)
  let heroUrl = url

  if (contentType === TYPE_CONTENT_VIDEO) {
    const primaryCourse = getPrimaryCourse({ data, userLanguage })
    heroUrl = primaryCourse ? primaryCourse.get('path', '') : url
  }

  let imageUrl = ''
  let fallbackImageUrl = ''

  try {
    if (optimizedImagesEnabled) {
      const fallbackImageUrlObject = new URL(image)
      const imageUrlObject = new URL(image)
      const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
      const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
      const fallbackImageOptions = {
        ...parsedFallbackImageQuery,
        w: imageWidth,
        h: imageHeight,
        fm: 'jpg',
        compress: 'auto',
      }
      const imageOptions = {
        ...parsedImageQuery,
        w: imageWidth,
        h: imageHeight,
        fm: 'webp',
        q: '100',
      }

      const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
      const stringifiedImageQuery = stringifyQuery(imageOptions)

      // set the search string back onto the fallback URL
      _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)

      // set the search string back onto the image URL
      _set(imageUrlObject, 'search', stringifiedImageQuery)

      fallbackImageUrl = fallbackImageUrlObject.toString()
      imageUrl = imageUrlObject.toString()
    }
  } catch (e) {
    fallbackImageUrl = image
    imageUrl = image
  }

  if (legacyData && contentType === TYPE_CONTENT_SERIES) {
    featuredEpisodePath = Map.isMap(data.get('featuredEpisode')) ? data.getIn(['featuredEpisode', 'data', 'path'], '') : ''
    featuredEpisodeFeature = Map.isMap(data.get('featuredEpisode')) ? data.getIn(['featuredEpisode', 'data', 'feature'], Map()) : Map()
  } else {
    featuredEpisodePath = Map.isMap(userNodeInfo) ? userNodeInfo.getIn(['featuredEpisode', 'data', 'path'], '') : ''
    featuredEpisodeFeature = Map.isMap(userNodeInfo) ? userNodeInfo.getIn(['featuredEpisode', 'data', 'feature'], Map()) : Map()
  }

  const feature = Map.isMap(data.get('feature')) ? data.get('feature', Map()) : Map()
  const path = contentType === TYPE_CONTENT_SERIES ? featuredEpisodePath : url
  const watchAccess = getWatchAccess({
    userSubscriptions: subscriptions,
    media: contentType === TYPE_CONTENT_SERIES ? featuredEpisodeFeature : feature,
    country,
  })
  const upgradeToWatch = isLiveAccessUpgradeToWatch({
    feature: contentType === TYPE_CONTENT_SERIES ? featuredEpisodeFeature : feature,
    subscriptions,
  })

  // Get the allowed subscriptions for the title and determine if it is a Gaia+ feature
  const contentPolicy = data.getIn(['contentPolicy', 'optionIds']) || List()
  const allowedSubscriptions = List.isList(contentPolicy) && contentPolicy.size > 0
    ? contentPolicy
    : featuredEpisodeFeature.getIn(['offerings', 'subscriptions'], List()) || List()
  const isGaiaPlus = allowedSubscriptions.size === 1
    && allowedSubscriptions.includes(SUBSCRIPTION_CODE_EVENTS_PLUS)

  return (
    <div className={
      getTileV2ElementClassName({
        display,
        hovered,
        contentType,
        baseClassName: 'tile-v2__hero',
      })
    }>
      {!hideLabel && <TileV2Label
        gaiaOriginal={gaiaOriginal}
        staticText={staticText}
        contentSubtype={contentSubtype}
        contentType={contentType}
        contentId={data.get('id')}
        upgradeToWatch={upgradeToWatch}
        isGuide={isGuide}
        isEvent={isEvent}
        isGaiaPlus={isGaiaPlus}
      />}
      <Link
        to={heroUrl}
        className={
          getTileV2ElementClassName({
            display,
            hovered,
            contentType,
            baseClassName: 'tile-v2__hero-link',
          })
        }
        scrollToTop
        onClick={(e) => handleTileV2OnClick(e, props, false, 'Tile Thumbnail')}
        target={isContentTypeProduct ? TARGET_BLANK : null}
        directLink={isContentTypeProduct}
      >
        {
          optimizedImagesEnabled
            ? <Picture
                fallbackSrc={fallbackImageUrl}
                width={imageWidth}
                height={imageHeight}
                alt={title}
                imageClassName={
                  getTileV2ElementClassName({
                    display,
                    hovered,
                    contentType,
                    baseClassName: 'tile-v2__hero-image',
                  })
                }
                loading={IMAGE_LOADING_LAZY}
                sources={[
                  {
                    type: IMAGE_TYPE_WEBP,
                    srcSet: imageUrl,
                  },
                ]}
              />
            : <img
                src={image}
                loading="lazy"
                className={
                  getTileV2ElementClassName({
                    display,
                    hovered,
                    contentType,
                    baseClassName: 'tile-v2__hero-image',
                  })
                }
                alt={title}
              />
        }
      </Link>
      <TileV2ActionButton
        {...props}
        isTouchable={isTouchable}
        contentType={contentType}
        path={path}
        watchAccess={watchAccess}
        upgradeToWatch={upgradeToWatch}
      />
      <TileV2ProgressBar {...props} />
    </div>
  )
}

function tileV2HeroPropsAreEqual (prevProps, nextProps) {
  const prevData = prevProps.data
  const nextData = nextProps.data
  const prevUserNodeInfo = prevProps.userNodeInfo
  const nextUserNodeInfo = nextProps.userNodeInfo
  const prevTouchable = prevProps.isTouchable
  const nextTouchable = nextProps.isTouchable

  return prevData.equals(nextData)
    && prevUserNodeInfo.equals(nextUserNodeInfo)
    && prevTouchable === nextTouchable
}

const MemoizedTileV2Hero = React.memo(TileV2Hero, tileV2HeroPropsAreEqual)

export default MemoizedTileV2Hero
